import { faArrowLeft, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQuery } from '@tanstack/react-query';
import { useContext } from 'react';
import { useParams } from 'react-router-dom';
import { Message } from '../components/ui/Message';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/Table';
import { Title } from '../components/ui/Title';
import { UserContext } from '../context/UserProvider';
import {ChangeRequestStatus, FuelTransactionStatus} from '../types/FuelTransaction';
import { Skeleton } from '../components/ui/Skeleton';
import { getFormattedDate } from '../lib/dateUtils';
import { Role } from '../types/Role';
import { routeUrls } from '../routes';
import { Link } from '../components/ui/Link';
import { ChangeRequestClient } from '../services/ChangeRequestClient';
import { MeasurementUnit } from '../components/ui/MeasurementUnit';

export const ChangeRequestPage = () => {
    const user = useContext(UserContext);
    const { id } = useParams();
    const adminOnEmployeeDashboard = user.userRole === Role.ADMIN && id;
    const { data, isPending, isError } = useQuery({
        queryKey: ['change-requests-submissions'],
        queryFn: () => ChangeRequestClient({ ...user, param: id }).getChangeRequests(),
        refetchOnMount: true
    });

    return (
        <div className="flex flex-col gap-6 md:gap-12">
            <div className="py-3 md:py-0 flex justify-between items-center">
                <Title>Status aanvragen</Title>
            </div>

            {adminOnEmployeeDashboard && (
                <div className="flex flex-col gap-6">
                    <Link href={`${routeUrls.DASHBOARD}/${id}`} hideAnchorIcon className="text-sm underline">
                        <FontAwesomeIcon icon={faArrowLeft} />
                        Werknemer dashboard
                    </Link>
                    <div className="w-full border-t-[1px] border-grey" />
                </div>
            )}

            {isError && <Message type="error">Er is iets misgegaan bij het ophalen van de aanvragen. Probeer het later nog een keer.</Message>}

            {isPending && (
                <>
                    <div className="flex w-full flex-col gap-2">
                        <Skeleton className="w-full h-[40px] rounded-sm" />
                        <Skeleton className="w-full h-[40px] rounded-sm" />
                        <Skeleton className="w-full h-[40px] rounded-sm" />
                        <Skeleton className="w-full h-[40px] rounded-sm" />
                    </div>
                </>
            )}

            {!isPending && !isError && data && (
                <>
                    <Table>
                        <TableHeader className="bg-secondary">
                            <TableRow>
                                <TableHead>Status</TableHead>
                                <TableHead>Transactiedatum</TableHead>
                                <TableHead>Locatie</TableHead>
                                <TableHead>Kilometerstand</TableHead>
                                <TableHead>Opmerking gebruiker</TableHead>
                                <TableHead>Opmerking beheerder</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {data &&
                                data.map((request, index) => {
                                    return (
                                        <TableRow className={`${(index + 1) % 2 === 0 ? 'bg-gray-100' : ''}`} key={index}>
                                            <TableCell>
                                                {request.status === ChangeRequestStatus.APPROVED && (
                                                    <small className={`text-white bg-primary px-2 py-2 rounded whitespace-nowrap`}>Goedgekeurd</small>
                                                )}
                                                {request.status === ChangeRequestStatus.PENDING && (
                                                    <small className={`text-white bg-active px-2 py-2 rounded whitespace-nowrap`}>
                                                        In afwachting
                                                    </small>
                                                )}
                                                {request.status === ChangeRequestStatus.REJECTED && (
                                                    <small className={`text-white bg-red-700 px-2 py-2 rounded whitespace-nowrap`}>Afgekeurd</small>
                                                )}
                                            </TableCell>
                                            <TableCell className="whitespace-nowrap">
                                                {request.fuelTransactionDate ? getFormattedDate(request.fuelTransactionDate) : 'Onbekend'}
                                            </TableCell>
                                            <TableCell>{request.location ? request.location : 'Handmatige tankbeurt'}</TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    <span className="line-through">
                                                        <MeasurementUnit measurement={request.kilometersOld} />
                                                    </span>
                                                    <FontAwesomeIcon icon={faArrowRight} />
                                                    <span>
                                                        <MeasurementUnit measurement={request.kilometersNew} />
                                                    </span>
                                                </div>
                                            </TableCell>
                                            <TableCell className="min-w-[200px]">{request.driverRemarks || '-'}</TableCell>
                                            <TableCell className="min-w-[200px]">{request.adminRemarks || '-'}</TableCell>
                                        </TableRow>
                                    );
                                })}
                        </TableBody>
                    </Table>
                </>
            )}
        </div>
    );
};
